import { Box, Chip, Divider, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { first, get, has, isEmpty, truncate } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertOctagon, Calendar, Edit, MapPin, PhoneCall } from 'react-feather';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FlagCountry from 'src/components/common/FlagCountry';
import IconFeather from 'src/components/common/Icon';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import ButtonBookmarkWhite from 'src/components/common/ButtonBookmarkWhite';
import FormBookmarkNote from 'src/features/Bookmark/FormBookmarkNote';
// import { usePrinter } from 'src/hooks/usePrinter';
// Utilities
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { convertToStartCase, getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import { useMutateCreateBookmark, useMutateUpdateBookmarkByDataUuid } from 'src/services/mutate/useMutateBookmark';
import { BookmarkTypes } from 'src/model/bookmark';
import { NATIONALITIES } from 'src/utils/constants/nationality';
import { EmptyText, MAX_LENGTH_CONTENT, UnknownText } from 'src/utils/constants/config';
import { useQueryGetBookmarkByDataUuid } from 'src/services/queries/useQueryBookmark';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { usePeopleContribute } from 'src/zustand/usePeopleContribute';
import { TypeContributePeople } from 'src/features/PeopleDetail/Common/config';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.25),
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  borderRadius: theme.shape.borderRadius * 2,
  color: theme.palette.common.white,
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  overflow: 'hidden',
}));

const StyledButtonShowMore = styled(Typography)(() => ({
  textDecoration: 'underline',
  cursor: 'pointer',
}));

interface IProps {
  dataPeople: IPeopleDetail;
  isFetching: boolean;
}

const PeopleDetailUserInfoPanel = ({ dataPeople, isFetching }: IProps) => {
  const theme = useTheme();
  const [visibleMore, setVisibleMore] = useState(false as boolean);

  const { handleOpenContribute } = usePeopleContribute((state) => state);
  const { data: dataBookmark } = useQueryGetBookmarkByDataUuid(BookmarkTypes.People, dataPeople?.id || '');
  const { mutateAsync: createBookmarkMutate, isPending: isPendingCreateBookmark } = useMutateCreateBookmark();
  const { mutateAsync: updateBookmarkMutate, isPending: isPendingUpdateBookmark } = useMutateUpdateBookmarkByDataUuid();

  const [note, setNote] = useState<string>('');
  const [isBookmark, setIsBookmark] = useState(false);
  const [isOpenBookmarkNoteForm, setIsOpenBookmarkNoteForm] = useState(false);

  const peopleDescription = dataPeople.description || '';

  const country = useMemo(() => {
    if (dataPeople.country_code) return getCountryByCountryCode(dataPeople.country_code || '');

    const countryCode = get(NATIONALITIES, dataPeople?.nationality || '', '');
    return getCountryByCountryCode(countryCode);
  }, [dataPeople]);

  useEffect(() => {
    // Fetch data bookmark done
    setIsBookmark(!isEmpty(dataBookmark?.data)); // If data bookmark is empty => This company is not marked yet.
    if (isEmpty(dataBookmark?.data)) {
      setNote('');
    } else {
      setNote(dataBookmark?.data?.note || '');
    }
  }, [dataBookmark?.data]);

  const handleCloseBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(false);
  };

  const handleOpenBookmarkNoteForm = () => {
    setIsOpenBookmarkNoteForm(true);
  };
  const handleSubmitBookmarkNoteForm = useCallback(async () => {
    const response = isBookmark
      ? await updateBookmarkMutate({
          type: BookmarkTypes.People,
          dataUuid: dataPeople?.id || '',
          body: {
            note,
          },
        })
      : await createBookmarkMutate({
          type: BookmarkTypes.People,
          body: {
            data_uuid: dataPeople?.id || '',
            note,
          },
        });
    if (response?.status_code === SUCCESS_CODE.CREATE) {
      setIsBookmark(true);
    }

    handleCloseBookmarkNoteForm();
  }, [dataPeople.id, note, isBookmark]);

  const renderDescription = () => {
    if (peopleDescription.length <= MAX_LENGTH_CONTENT.PEOPLE_INFORMATION) {
      return (
        <TypographyWhite align="left" variant="body2">
          {peopleDescription}
        </TypographyWhite>
      );
    }

    if (!visibleMore) {
      return (
        <TypographyWhite align="left" variant="body2">
          {truncate(peopleDescription, { length: MAX_LENGTH_CONTENT.PEOPLE_INFORMATION })}{' '}
          <StyledButtonShowMore
            display={'inline'}
            onClick={() => setVisibleMore(!visibleMore)}
            variant="subtitle2"
            color={'inherit'}
          >
            Show More
          </StyledButtonShowMore>
        </TypographyWhite>
      );
    } else {
      return (
        <TypographyWhite align="left" variant="body2">
          {peopleDescription}{' '}
          <StyledButtonShowMore
            display={'inline'}
            onClick={() => setVisibleMore(!visibleMore)}
            variant="subtitle2"
            color={'inherit'}
          >
            Show Less
          </StyledButtonShowMore>
        </TypographyWhite>
      );
    }
  };

  if (isFetching) {
    return (
      <StyledBox flexDirection="row">
        <LoadingSkeleton count={5} height={30} />
      </StyledBox>
    );
  }

  return (
    <StyledBox>
      <Stack direction="row" spacing={2} alignItems="center">
        <Stack flex={1}>
          <Typography variant="h5" color={'inherit'}>
            {convertToStartCase(dataPeople?.full_name || EmptyText)}
          </Typography>
          <Box mt={1} />
          {dataPeople?.position && (
            <Typography variant="caption2" color="text.secondary" align="left">
              {convertToStartCase(dataPeople?.position || UnknownText)}
            </Typography>
          )}
          <Box mt={1} />
          <Stack gap={0.5} direction="row" flexWrap="wrap">
            {!isEmpty(dataPeople.category) &&
              dataPeople.category.map((item) => (
                <Chip
                  key={`category-${dataPeople.id}-${item}`}
                  label={item}
                  color="secondary"
                  size="small"
                  sx={{
                    color: theme.palette.secondary.contrastText,
                    backgroundColor: theme.palette.secondary.main,
                    '&:hover': {
                      color: theme.palette.secondary.contrastText,
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                />
              ))}
          </Stack>
          <Box mt={1} />
          <Typography color={'inherit'}>{renderDescription()}</Typography>
          <Box mt={3} />
          <Stack direction={'row'} gap={2}>
            <Tooltip title="Coming Soon!">
              <ButtonWithLoading
                variant="outlined"
                color="inheritWhite"
                isRounded
                endIcon={<IconFeather icon={<Edit />} muiProps={{ width: '16' }} />}
                size="medium"
                onClick={() => handleOpenContribute(TypeContributePeople.ContactPeopleProfile)}
              >
                Contribute DATA
              </ButtonWithLoading>
            </Tooltip>
            {/* <Tooltip placement="top" title="Print">
              <IconButton
                size="large"
                sx={{
                  border: '1px solid white',
                }}
                color="inherit"
                onClick={() => handlePrint()}
              >
                <IconFeather icon={<Printer />} svgProps={{ fontSize: 'small' }} />
              </IconButton>
            </Tooltip> */}
            <ButtonBookmarkWhite
              isMarked={isBookmark}
              buttonProps={{
                onClick: () => handleOpenBookmarkNoteForm(),
              }}
            />
          </Stack>
        </Stack>
        <Divider flexItem orientation="vertical" />
        <Stack gap={2} flexBasis={254}>
          {has(dataPeople?.addresses, '[0].full_address') && (
            <InforItem
              icon={<MapPin />}
              label={(get(first(dataPeople.addresses), 'full_address', EmptyText) || '').toUpperCase()}
              isFeatherIcon
            />
          )}
          {!isEmpty(dataPeople?.phone_numbers) && (
            <InforItem
              icon={<PhoneCall />}
              label={getArrayDefault(dataPeople?.phone_numbers).join(', ')}
              isFeatherIcon
            />
          )}
          {dataPeople?.birthday && (
            <InforItem
              icon={<Calendar />}
              label={dataPeople?.birthday || UnknownText}
              isFeatherIcon
              tooltip="Birth Year"
            />
          )}
          <InforItem
            icon={<FlagCountry width={12} code={country.code} colorDefaultFlag={theme.palette.common.white} />}
            label={convertToStartCase(country.label || UnknownText)}
          />
          {dataPeople?.is_sanctioned && (
            <InforItem
              icon={<AlertOctagon />}
              label={dataPeople?.is_sanctioned ? 'Sanctioned' : 'Not Found'}
              isFeatherIcon
              tooltip="Sanctioned Person"
            />
          )}
        </Stack>
      </Stack>
      <FormBookmarkNote
        open={isOpenBookmarkNoteForm}
        handleClose={handleCloseBookmarkNoteForm}
        value={note}
        onChange={(value) => setNote(value)}
        handleSubmit={() => handleSubmitBookmarkNoteForm()}
        isLoading={isPendingCreateBookmark || isPendingUpdateBookmark}
      />
    </StyledBox>
  );
};

export default PeopleDetailUserInfoPanel;

const InforItem = ({
  icon,
  label,
  tooltip,
  isFeatherIcon,
}: {
  icon: ReactNode;
  tooltip?: string;
  label: ReactNode;
  isFeatherIcon?: boolean;
}) => {
  return (
    <Stack gap={1} direction={'row'} alignItems="center">
      {isFeatherIcon ? <IconFeather muiProps={{ sx: { fontSize: 12 } }} icon={icon} /> : icon}
      <Typography variant="caption" color={'inherit'}>
        {label} {tooltip && <TooltipFormLabel color="inherit" title={tooltip} />}
      </Typography>
    </Stack>
  );
};
